import React from 'react';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS, toneStyle } from './tokens';
import type { PortfolioPad } from '../../lib/portfolioApi';
import { pendingFemaLabel } from '../../lib/fema-pending';

// Compact research row for the Crown Pine page's narrow right panel. The unit
// is a single buildable PAD — one contiguous clean piece exploded out of an
// ownership block — ranked by its own acreage against every other Crown Pine
// pad. Shows what matters for siting a pad: acreage / slope / flood, plus the
// parent block it sits in (name + knockout state). Distinct from the Integra
// PortfolioRow, which is per-assembly and carries the IC grade columns.
function knockoutTone(count: number): { fg: string; label: string } {
  if (count === 0) return { fg: toneStyle('pass').fg, label: 'Clean' };
  if (count === 1) return { fg: toneStyle('caution').fg, label: '1 gate' };
  return { fg: toneStyle('fail').fg, label: `${count} gates` };
}

const fmt = (n: number | null | undefined, suffix = '') =>
  n == null ? '—' : `${Math.round(Number(n)).toLocaleString()}${suffix}`;

const fmtDistance = (n: number | null | undefined) =>
  n == null ? '—' : `${Number(n).toFixed(Number(n) < 10 ? 2 : 1)} mi`;

interface Props {
  pad: PortfolioPad;
  rowNumber: number;
  isActive: boolean;
  isHovered: boolean;
  isExpanded: boolean;
  onSelect: (padId: string) => void;
  onHover: (padId: string | null) => void;
  onToggleExpand: (padId: string) => void;
}

export const CrownPineRow: React.FC<Props> = ({
  pad, rowNumber, isActive, isHovered, isExpanded, onSelect, onHover, onToggleExpand,
}) => {
  const koCount = pad.parent?.knockout_count ?? 0;
  const kt = knockoutTone(koCount);
  const acres = pad.acres;
  const slope = pad.avg_slope_pct;
  const flood = pad.flood_pct;
  const county = pad.parent?.county;
  const state = pad.parent?.state;
  const blockName = pad.parent?.name;
  const grade = pad.parent?.grade;
  const shortlist = pad.shortlist;

  if (shortlist) {
    const pendingFlood = shortlist.flood_basis?.status === 'pending' ? shortlist.flood_basis : null;
    const tierTone = toneStyle(shortlist.tier === 'Advance' ? 'pass' : 'caution');
    const campus = shortlist.campus;
    const pipe = shortlist.pipeline;
    const highway = shortlist.highway;
    const floodLabel = campus.flood_pct == null
      ? 'not mapped'
      : `${Number(campus.flood_pct).toFixed(1)}%`;
    const terrainParts = [
      campus.avg_slope_pct == null ? null : `${Number(campus.avg_slope_pct).toFixed(1)}% avg slope`,
      campus.pct_under_5_pct == null ? null : `${Number(campus.pct_under_5_pct).toFixed(0)}% under 5%`,
      `flood ${floodLabel}`,
      campus.estimated_aquatic_fill_ac && campus.estimated_aquatic_fill_ac > 0
        ? `${Number(campus.estimated_aquatic_fill_ac).toFixed(2)} ac est. fill`
        : null,
    ].filter(Boolean).join(' · ');
    const floodContext = shortlist.outside_sfha_ac == null
      ? 'Block SFHA acreage unavailable'
      : `${fmt(shortlist.outside_sfha_ac)} block ac outside SFHA`;
    const femaCoverage = pendingFlood ? pendingFemaLabel(pendingFlood.effective_date) : shortlist.fema_coverage_pct == null
      ? 'FEMA coverage unavailable'
      : `${Number(shortlist.fema_coverage_pct).toFixed(0)}% FEMA coverage`;
    const effective = shortlist.buildable?.effective;

    return (
      <div
        data-parcel-row-id={pad.id}
        onClick={() => onSelect(pad.id)}
        onMouseEnter={() => onHover(pad.id)}
        onMouseLeave={() => onHover(null)}
        style={{
          border: `1px solid ${isActive ? REPORT.accent : REPORT.rule}`,
          borderLeft: `3px solid ${tierTone.fg}`,
          background: isActive ? REPORT.surface : isHovered ? '#fbfaf8' : REPORT.white,
          padding: '9px 10px 10px', cursor: 'pointer',
          transition: 'background 0.12s ease, border-color 0.12s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9, fontWeight: 700,
            color: REPORT.tertiary, minWidth: 18,
          }}>
            {String(shortlist.screen_rank).padStart(2, '0')}
          </span>
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8, fontWeight: 700,
            letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap',
            color: tierTone.fg, background: tierTone.bgWash,
            border: `1px solid ${tierTone.border}`, borderRadius: 3, padding: '1px 5px',
          }}>
            {shortlist.tier}
          </span>
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.serif, fontSize: 14, color: REPORT.heading,
            flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {shortlist.block_id}
          </span>
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, color: REPORT.tertiary,
            whiteSpace: 'nowrap',
          }}>
            {shortlist.county || county} County
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 5, paddingLeft: 25 }}>
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 13, fontWeight: 700, color: tierTone.fg }}>
            {fmt(campus.acres)}
          </span>
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.2, letterSpacing: '0.05em', color: REPORT.tertiary, textTransform: 'uppercase' }}>
            first-phase ac
          </span>
          <span style={{ color: REPORT.rule, margin: '0 1px' }}>|</span>
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 10, fontWeight: 700, color: REPORT.heading }}>
            {fmt(shortlist.gross_legal_acres)}
          </span>
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.2, letterSpacing: '0.05em', color: REPORT.tertiary, textTransform: 'uppercase' }}>
            gross block ac
          </span>
        </div>

        <div style={{
          marginTop: 4, paddingLeft: 25, fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 8.8, color: REPORT.secondary,
        }}>
          <div>{floodContext} · {femaCoverage}</div>
          {effective && effective.crossings > 0 && (
            <div style={{ marginTop: 1 }}>
              Buildable screened: {fmt(shortlist.buildable?.total_ac)} ac in {fmt(shortlist.buildable?.piece_count)} piece{shortlist.buildable?.piece_count === 1 ? '' : 's'}
            </div>
          )}
          {effective && effective.crossings > 0 && (
            <div style={{ marginTop: 1 }}>
              Effective contiguous: {fmt(effective.total_ac)} ac in {effective.piece_count} piece{effective.piece_count === 1 ? '' : 's'} · {effective.crossings} stream crossing{effective.crossings === 1 ? '' : 's'}
            </div>
          )}
          <div style={{ marginTop: 1 }}>{terrainParts}</div>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 7,
          padding: '6px 7px', background: REPORT.surface, borderTop: `1px solid ${PORTFOLIO_TOKENS.ruleSoft}`,
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.8, color: REPORT.secondary,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 7.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: REPORT.tertiary }}>Energy Transfer pipeline</div>
            <div style={{ marginTop: 1, color: REPORT.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pipe.name || '—'}{pipe.diameter_in == null ? '' : ` · ${Number(pipe.diameter_in).toLocaleString(undefined, { maximumFractionDigits: 2 })}\u2033`} · {fmtDistance(pipe.campus_distance_mi)}
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 7.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: REPORT.tertiary }}>Highway access</div>
            <div style={{ marginTop: 1, color: REPORT.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {highway.name || '—'} · {fmtDistance(highway.campus_distance_mi)}
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: '76px 1fr', columnGap: 7, rowGap: 3,
          marginTop: 6, paddingLeft: 25, fontSize: 9.5, lineHeight: 1.35,
        }}>
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 7.8, letterSpacing: '0.07em', textTransform: 'uppercase', color: REPORT.tertiary }}>Expansion</span>
          <span style={{ color: REPORT.body }}>{shortlist.expansion_status || '—'}</span>
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 7.8, letterSpacing: '0.07em', textTransform: 'uppercase', color: REPORT.tertiary }}>Primary gate</span>
          <span style={{ color: REPORT.body }}>{shortlist.primary_gate || '—'}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-parcel-row-id={pad.id}
      onClick={() => onSelect(pad.id)}
      onMouseEnter={() => onHover(pad.id)}
      onMouseLeave={() => onHover(null)}
      style={{
        border: `1px solid ${isActive ? REPORT.accent : REPORT.rule}`,
        borderLeft: `3px solid ${kt.fg}`,
        background: isActive ? REPORT.surface : isHovered ? '#fbfaf8' : REPORT.white,
        padding: '8px 10px',
        cursor: 'pointer',
        transition: 'background 0.12s ease, border-color 0.12s ease',
      }}
    >
      {/* Line 1 — rank + county + parent grade + expand */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9, fontWeight: 700,
          color: REPORT.tertiary, minWidth: 18,
        }}>
          {String(rowNumber).padStart(2, '0')}
        </span>
        {pad.site && (
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8, fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap',
            color: '#fff', background: REPORT.accent, borderRadius: 3, padding: '2px 6px',
          }}>
            Site {pad.site}
          </span>
        )}
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.serif, fontSize: 14, color: REPORT.heading,
          flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {county} County
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, color: REPORT.tertiary, marginLeft: 6 }}>
            {state}
          </span>
        </span>
        {grade && (
          <span title={`Parent block grade ${grade}`} aria-label={`Parent block grade ${grade}`} style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.5, fontWeight: 700,
            color: REPORT.heading, letterSpacing: '0.04em',
          }}>
            Parent {grade}
          </span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleExpand(pad.id); }}
          aria-label="Toggle details"
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer', color: REPORT.tertiary,
            fontSize: 11, padding: '0 2px', transform: isExpanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease', lineHeight: 1,
          }}
        >
          ⌄
        </button>
      </div>

      {/* Line 2 — THIS pad's buildable acreage (headline) + parent block name */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 3, paddingLeft: 26 }}>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 13, fontWeight: 700, color: toneStyle('pass').fg }}>
          {fmt(acres)}
        </span>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, letterSpacing: '0.06em', color: REPORT.tertiary, textTransform: 'uppercase' }}>
          buildable ac · pad
        </span>
        {blockName && (
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9, color: REPORT.secondary, marginLeft: 'auto',
            minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '52%',
          }}>
            {blockName}
          </span>
        )}
      </div>

      {/* Line 3 — pad slope · pad flood · parent knockout state */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, marginTop: 4, paddingLeft: 26,
        fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.5, color: REPORT.secondary,
      }}>
        <span>Slope <span style={{ color: REPORT.heading }}>{slope == null ? 'n/a' : `${Number(slope).toFixed(1)}%`}</span></span>
        <span style={{ color: REPORT.tertiary }}>·</span>
        <span>Flood <span style={{ color: REPORT.heading }}>{flood == null ? 'n/a' : `${Number(flood).toFixed(1)}%`}</span></span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto', color: kt.fg }}>
          <span style={{ width: 7, height: 7, borderRadius: 4, background: kt.fg, display: 'inline-block' }} />
          {kt.label}
        </span>
      </div>
    </div>
  );
};

export default CrownPineRow;
